import {
  PremiumFeature,
  ProductName,
  ProductDesc,
  ProductInterval,
  ProductShortName,
  Product,
} from './types';

export class FeatureSet {
  private products: Map<number, ProductName> = new Map();

  // a map between product numbers and an array containing what features that product adds
  private productFeatures: Map<number, Set<PremiumFeature>> = new Map();

  // a map from product short names to their ids
  private productShortNames: Map<ProductShortName, number> = new Map();

  // # a map from product ids to the price of the product
  private prices: Map<number, number> = new Map();

  // a map from product ids to the billing interval ("monthly" or "yearly")
  private intervals: Map<number, ProductInterval> = new Map();

  // a list of per-user products
  private perUserProducts: Map<number, boolean> = new Map();

  // a map of smart billed products
  private perActiveUserProducts: Map<number, boolean> = new Map();

  // a map with products that pay with a purchase order
  private purchaseOrderProducts: Map<number, boolean> = new Map();

  // features everyone should have enabled
  private globalFeatures: Set<PremiumFeature> = new Set();

  // products that should ask you to upgrade to the latsest
  private upgradeProducts: Map<number, boolean> = new Map();
  private yearlyEquivalents: Map<number, number> = new Map();

  constructor(
    products: Map<number, Product>,
    globalFeatures: Set<PremiumFeature> = new Set(),
  ) {
    for (const [productNum, productVal] of products) {
      const product: Product = {
        interval: ProductInterval.Monthly,
        hidden: false,
        prebill: true,
        perUser: false,
        upgrade: false,
        ...productVal,
      };

      this.globalFeatures = globalFeatures;
      this.products.set(productNum, product.name);

      if (!product.hidden) {
        this.productShortNames.set(productVal.shortName, productNum);
      }

      this.perUserProducts.set(productNum, productVal.perUser || false);
      this.perActiveUserProducts.set(
        productNum,
        productVal.perActiveUser || false,
      );

      this.productFeatures.set(productNum, productVal.features || []);

      if (productVal.price) {
        this.prices.set(productNum, productVal.price);
      }

      if (productVal.interval) {
        this.intervals.set(productNum, productVal.interval);
      }

      this.upgradeProducts.set(productNum, productVal.upgrade || false);

      if (productVal.yearlyEquivalent) {
        this.yearlyEquivalents.set(productNum, productVal.yearlyEquivalent);
      }

      this.purchaseOrderProducts.set(
        productNum,
        productVal.purchaseOrder || false,
      );
    }
  }

  /**
   * Please check the `premiumFeatures` of the Member/Org/Enterprise model you're looking
   * at rather than using this method
   *
   * @deprecated
   */
  isFeatureEnabled = (feature: PremiumFeature, productNumber?: number) => {
    if (this.globalFeatures.has(feature)) {
      return true;
    }

    const key = this.convertToId(productNumber);

    if (typeof key === 'undefined') {
      return false;
    }

    const features = this.productFeatures.get(key);

    if (!features) {
      throw new Error(
        `FeatureSet::isFeatureEnabled called on an unrecognized product: ${productNumber}`,
      );
    }

    return features.has(feature);
  };

  private convertToId(product?: ProductDesc): number | undefined {
    if (typeof product === 'undefined') {
      return undefined;
    } else if (typeof product === 'string') {
      if (/^\d+$/.test(product)) {
        return parseInt(product, 10);
      } else {
        return this.productShortNames.get(product as ProductShortName);
      }
    } else {
      return product as number;
    }
  }

  hasProduct(product?: number) {
    return !!this.products.has(this.convertToId(product) as number);
  }

  getProductNumber(product?: ProductDesc) {
    return this.convertToId(product);
  }

  getProductName(product?: ProductDesc) {
    return this.products.get(this.convertToId(product) as number);
  }

  /**
   * Use price quotes endpoints to get pricing information
   *
   * @deprecated
   */
  getPrice(product?: ProductDesc) {
    return this.prices.get(this.convertToId(product) as number);
  }

  getInterval(product?: ProductDesc) {
    return this.intervals.get(this.convertToId(product) as number);
  }

  isMonthly(product?: ProductDesc) {
    return this.getInterval(product) === 'monthly';
  }

  isYearly(product?: ProductDesc) {
    return this.getInterval(product) === 'yearly';
  }

  isPerActiveUser(product: ProductDesc) {
    return !!this.perActiveUserProducts.get(
      this.convertToId(product) as number,
    );
  }

  isPerUser(product?: ProductDesc) {
    return !!this.perUserProducts.get(this.convertToId(product) as number);
  }

  isUpgrade(product?: ProductDesc) {
    return !!this.upgradeProducts.get(this.convertToId(product) as number);
  }

  usesPurchaseOrder(product?: ProductDesc) {
    return !!this.purchaseOrderProducts.get(
      this.convertToId(product) as number,
    );
  }

  getYearlyEquivalent(product?: ProductDesc) {
    return this.yearlyEquivalents.get(this.convertToId(product) as number);
  }

  /**
   * Use price quotes endpoints to get pricing information
   *
   * @deprecated
   */
  getYearlyDiscountPercent(
    productYearly: ProductDesc,
    productMonthly: ProductDesc,
  ) {
    const priceYearly = this.getPrice(productYearly);
    let priceMonthly = this.getPrice(productMonthly);

    if (priceYearly && priceMonthly) {
      priceMonthly *= 12;

      return Math.round((100 * (priceMonthly - priceYearly)) / priceMonthly);
    }
  }
}
